import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import Favicon from 'react-favicon';
import { styled } from '@mui/material/styles';
import { Box, Drawer, Typography, Avatar, IconButton } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';

import account from '../../_mock/account';
import useResponsive from '../../hooks/useResponsive';
import Scrollbar from '../../components/Scrollbar';
import NavSection from '../../components/NavSection';
import AppService from '../../services/AppService';
import { useAuth } from '../../context/AuthContext';
import AuthService from '../../services/DashboardUserService';
import BoxLoader from '../../components/BoxLoader';

// ── Design tokens ─────────────────────────────────────────────────────────────
// Brand color is driven by the active theme via CSS variables (see src/theme/index.js).
const VIOLET = 'var(--color-primary-main)';
const VIOLET_LIGHT = 'var(--color-primary-light)';
const VIOLET_BG = 'rgba(var(--color-primary-main-rgb), 0.08)';    // pale brand tint for active items
const VIOLET_DARK = 'var(--color-primary-dark)';
const VIOLET_RGBA = (opacity) => `rgba(var(--color-primary-main-rgb), ${opacity})`;
const WHITE = '#FFFFFF';
const SURFACE = '#F8FAFC';    // almost white hover bg
const BORDER = '#E2E8F0';
const T1 = '#0F172A';
const T2 = '#475569';
const T3 = '#94A3B8';
const DRAWER_W = 252;

// ── Styled components ──────────────────────────────────────────────────────────
const RootStyle = styled('div', {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})(({ theme, collapsed }) => ({
  [theme.breakpoints.up('lg')]: { flexShrink: 0, width: collapsed ? 0 : DRAWER_W },
}));

const AppCard = styled(Paper)(({ selected }) => ({
  background: selected ? `linear-gradient(135deg, ${VIOLET}, ${VIOLET_DARK})` : SURFACE,
  border: `1.5px solid ${selected ? VIOLET : BORDER}`,
  borderRadius: 12,
  padding: '14px 10px',
  textAlign: 'center',
  cursor: 'pointer',
  boxShadow: 'none',
  transition: 'all 0.18s ease',
  '&:hover': {
    background: selected ? undefined : VIOLET_BG,
    borderColor: VIOLET_LIGHT,
    transform: 'translateY(-2px)',
    boxShadow: `0 4px 14px ${VIOLET_RGBA(0.12)}`,
  },
}));

const AppBlock = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  padding: '10px 14px',
  margin: '0 10px',
  borderRadius: 12,
  background: SURFACE,
  border: `1.5px solid ${BORDER}`,
  cursor: 'pointer',
  transition: 'all 0.18s ease',
  '&:hover': {
    borderColor: VIOLET_LIGHT,
    background: VIOLET_BG,
    boxShadow: `0 2px 10px ${VIOLET_RGBA(0.08)}`,
  },
}));

const StyledDialog = styled(Dialog)(() => ({
  '& .MuiDialog-paper': {
    borderRadius: 18,
    boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
    border: `1px solid ${BORDER}`,
    overflow: 'hidden',
  },
  '& .MuiDialogContent-root': { padding: 16 },
}));

// ── Sidebar drawer paper styles ────────────────────────────────────────────────
// WHITE sidebar — clean, no color, easy to read
const paperSx = {
  width: DRAWER_W,
  background: WHITE,
  borderRight: `1px solid ${BORDER}`,
  boxShadow: '2px 0 16px rgba(0,0,0,0.04)',
  // Override NavSection list items
  '& .MuiListItemButton-root': {
    borderRadius: '10px',
    mx: '6px',
    mb: '2px',
    color: T2,
    fontWeight: 500,
    fontSize: '0.875rem',
    transition: 'all 0.15s ease',
    '&:hover': {
      background: SURFACE,
      color: VIOLET,
      '& .MuiListItemIcon-root': { color: VIOLET },
    },
  },
  // Active item — violet left border + pale violet background
  '& .MuiListItemButton-root.active': {
    background: VIOLET_BG,
    color: VIOLET,
    fontWeight: 700,
    borderLeft: `3px solid ${VIOLET}`,
    paddingLeft: '11px',
    '& .MuiListItemIcon-root': { color: VIOLET },
    '&:hover': { background: VIOLET_BG },
  },
  '& .MuiListItemIcon-root': { color: T3, minWidth: 36, transition: 'color 0.15s' },
  '& .MuiListSubheader-root': {
    background: 'transparent',
    color: T3,
    fontSize: '0.6rem',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    fontWeight: 700,
  },
};

// ── Component ──────────────────────────────────────────────────────────────────
export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar, collapsed = false }) {
  const { authState } = useAuth();
  const [navigationMenu, setNavigationMenu] = useState([]);
  const [loading, setLoading] = useState(false);
  const isDesktop = useResponsive('up', 'lg');
  const [open, setOpen] = useState(null);
  const [appData, setAppData] = useState([]);
  const [newLists, setNewList] = useState([]);
  const [displayAssignedAp, setDisplayAssignedAp] = useState('');
  const [backgroundMenu, setBackgroundMenu] = useState('');
  const [loadingBar, setLoadingBar] = useState(false);
  const [currentLogo, setCurrentLogo] = useState('');
  const [faviconUrl, setFaviconUrl] = useState('');

  const handleOpen = (e) => setOpen(e.currentTarget);
  const handleClose = () => setOpen(null);

  const handleChange = async (appId) => {
    const Obj = JSON.parse(localStorage.getItem('user'));
    if (appId === Obj.default_app) return;
    setLoadingBar(true);
    const match = appData.find((a) => a.app_id === appId);
    Obj.default_app = match.app_id;
    localStorage.setItem('user', JSON.stringify(Obj));
    const res = await AuthService.changeApp({ app_id: appId.toString() });
    if (res) window.location.reload(false);
  };

  const getNavigationMenu = async () => {
    setLoading(true);
    setNavigationMenu(authState.user.menu_items);
    setLoading(false);
  };

  const getData = async () => {
    const { data } = await AppService.list();
    setAppData(data.records);
    const assigned = authState.user.assigned_apps;
    setNewList(data.records.filter((el) => assigned.includes(el.app_id)));
    const current = data.records.find((el) => authState.user.default_app.includes(el.app_id));
    setBackgroundMenu(current.app_name);
    setDisplayAssignedAp(current.app_name);
    setCurrentLogo(current.short_code);
    setFaviconUrl(`${account.faviconURL}${current.short_code}.png`);
  };

  useEffect(() => { getData(); getNavigationMenu(); }, []); // eslint-disable-line
  useEffect(() => { if (isOpenSidebar) onCloseSidebar(); }, []); // eslint-disable-line

  const renderContent = (
    <>
      {/* ── Logo / Brand ── */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.25,
        px: 2.5, py: 2.5,
        borderBottom: `1px solid ${BORDER}`,
      }}>
        {/* <Box sx={{
          width: 36, height: 36, borderRadius: '10px', flexShrink: 0,
          background: `linear-gradient(135deg, ${VIOLET}, ${VIOLET_DARK})`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `0 4px 12px ${VIOLET_RGBA(0.3)}`,
        }}>
          <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '0.95rem' }}>C</Typography>
        </Box> */}
        <Box>
          <Typography sx={{ color: T1, fontWeight: 800, fontSize: '0.95rem', lineHeight: 1.15, letterSpacing: '-0.01em' }}>
            Misc App
          </Typography>
          <Typography sx={{ color: T3, fontSize: '0.6rem', letterSpacing: '0.12em', fontWeight: 700 }}>
            ADMIN PANEL
          </Typography>
        </Box>
      </Box>

      {/* ── App switcher ── */}
      <Box sx={{ px: 1, pt: 1.25, pb: 0.5 }}>
        <AppBlock onClick={handleOpen}>
          <Avatar
            src={`${account.logoURL}${currentLogo}.png`}
            sx={{ width: 30, height: 30, border: `1.5px solid ${BORDER}` }}
          />
          <Box sx={{ ml: 1.5, flex: 1, overflow: 'hidden' }}>
            <Typography noWrap sx={{ color: T1, fontWeight: 700, fontSize: '0.83rem', lineHeight: 1.2 }}>
              {displayAssignedAp}
            </Typography>
            <Typography sx={{ color: VIOLET, fontSize: '0.65rem', fontWeight: 600 }}>
              Switch app ›
            </Typography>
          </Box>
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: '#22C55E', boxShadow: '0 0 5px rgba(34,197,94,0.5)' }} />
        </AppBlock>
      </Box>

      {/* Divider */}
      <Box sx={{ height: '1px', mx: 2.5, my: 1, background: BORDER }} />

      {/* ── App picker dialog ── */}
      <StyledDialog onClose={handleClose} open={Boolean(open)}>
        <DialogTitle sx={{ color: T1, fontWeight: 800, fontSize: '1rem', py: 1.75, px: 2.5, borderBottom: `1px solid ${BORDER}` }}>
          Choose Application
          <IconButton onClick={handleClose} size="small"
            sx={{ position: 'absolute', right: 10, top: 12, color: T3, borderRadius: '8px' }}>
            ✕
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ width: { xs: '100%', sm: 460 }, minHeight: 240, pt: 2 }}>
          <Grid container spacing={1.5}>
            {newLists.map((name, i) => (
              <Grid item xs={6} key={i}>
                <AppCard selected={backgroundMenu === name.app_name} onClick={() => handleChange(name.app_id)}>
                  <Avatar sx={{ display: 'inline-flex', mb: 0.75, width: 34, height: 34 }}
                    src={`${account.logoURL}${name.short_code}.png`} />
                  <Typography sx={{
                    color: backgroundMenu === name.app_name ? '#fff' : T1,
                    fontWeight: 600, fontSize: '0.72rem', wordBreak: 'break-word',
                  }}>
                    {name.app_name}
                  </Typography>
                </AppCard>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <BoxLoader loading={loadingBar} />
      </StyledDialog>

      {/* ── Navigation ── */}
      <Scrollbar sx={{ height: 1, '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' } }}>
        <NavSection navConfig={navigationMenu} loading={loading} />
        <Box sx={{ flexGrow: 1 }} />

        {/* ── Footer badge ── */}
        <Box sx={{ p: 2 }}>
          {/* <Box sx={{
            borderRadius: '12px', p: 1.5, textAlign: 'center',
            background: VIOLET_BG,
            border: `1px solid ${VIOLET_RGBA(0.15)}`,
          }}>
            <Typography sx={{ color: T3, fontSize: '0.6rem', letterSpacing: '0.08em', fontWeight: 700 }}>
              POWERED BY
            </Typography>
            <Typography sx={{ color: VIOLET, fontSize: '0.78rem', fontWeight: 800, mt: 0.25 }}>
              Cleno AI Engine
            </Typography>
          </Box> */}
        </Box>
      </Scrollbar>
    </>
  );

  return (
    <RootStyle collapsed={collapsed}>
      <Favicon url={faviconUrl} />
      {!isDesktop && (
        <Drawer open={isOpenSidebar} onClose={onCloseSidebar}
          PaperProps={{ sx: { width: DRAWER_W, ...paperSx } }}>
          {renderContent}
        </Drawer>
      )}
      {isDesktop && (
        <Drawer open={!collapsed} variant="persistent"
          PaperProps={{ sx: { width: DRAWER_W, ...paperSx } }}>
          {renderContent}
        </Drawer>
      )}
    </RootStyle>
  );
}

DashboardSidebar.propTypes = { isOpenSidebar: PropTypes.bool, onCloseSidebar: PropTypes.func, collapsed: PropTypes.bool };
